import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Megaphone, Clapperboard, Mic2 } from "lucide-react";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";
import { TrackCard } from "../components/TrackCard";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1518972559570-7cc1309f3229?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwcmVjb3JkaW5nJTIwc3R1ZGlvJTIwZXF1aXBtZW50JTIwbWl4aW5nJTIwZGVza3xlbnwwfHx8fDE3ODY2MDg0NzR8MA&ixlib=rb-4.1.0&q=85";

const HERO_LINES = ["LA MUSIQUE", "QUI PORTE", "VOS IMAGES."];

const CHAPTERS = [
  { n: "01", title: "Écoute", text: "Chaque morceau naît au studio, entre machines analogiques et nuits longues. Rien de générique : des textures pensées pour raconter." },
  { n: "02", title: "Image", text: "Une bonne synchro ne s'entend pas, elle se ressent. Je compose pour le montage : respirations, montées, silences utiles." },
  { n: "03", title: "Licence", text: "Un prix unique par morceau. Licence de synchronisation non exclusive, fichiers masters livrés instantanément après paiement." },
];

const USAGES = [
  { icon: Megaphone, title: "Publicité", text: "Spots TV, digitaux et réseaux sociaux. Des accroches courtes qui marquent dès la première seconde." },
  { icon: Clapperboard, title: "Film & Série", text: "Thèmes, tensions et respirations pour le cinéma, la bande-annonce et le documentaire." },
  { icon: Mic2, title: "Artistes", text: "Instrumentales et toplines prêtes à accueillir une voix, pour EP, singles et sessions." },
];

const Home = () => {
  const [tracks, setTracks] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <SEO />
      <section ref={heroRef} className="relative flex min-h-screen items-center justify-center overflow-hidden">
        <motion.img
          src={HERO_IMG}
          alt="Console de mixage en studio"
          style={{ y: bgY }}
          className="absolute inset-0 h-[125%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-black/40" />
        <div className="relative z-10 px-5 text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mb-6 text-xs uppercase tracking-[0.4em] text-amber-400"
          >
            La plateforme des artistes — Musiques libres de droit pour l'image
          </motion.p>
          <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tighter sm:text-7xl lg:text-8xl">
            {HERO_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden py-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className={`block ${i === 2 ? "italic text-amber-500" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mx-auto mt-10 grid max-w-3xl overflow-hidden border border-white/10 sm:grid-cols-2"
            data-testid="hero-dual-intro"
          >
            <div className="bg-black/55 p-6 backdrop-blur-sm">
              <p className="text-[10px] uppercase tracking-[0.35em] text-amber-500">Artistes</p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-300">
                Vendez vos créations en toute autonomie : publiez vos morceaux,
                fixez vos prix, encaissez vos licences.
              </p>
              <Link
                to="/devenir-artiste"
                data-testid="hero-cta-more"
                className="mt-4 inline-block text-[11px] uppercase tracking-[0.2em] text-amber-400 underline-offset-4 transition-colors hover:underline"
              >
                En savoir plus
              </Link>
            </div>
            <div className="border-t border-white/10 bg-black/55 p-6 backdrop-blur-sm sm:border-l sm:border-t-0">
              <p className="text-[10px] uppercase tracking-[0.35em] text-amber-500">Marques, réalisateurs & créateurs de contenus</p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-300">
                Achetez des musiques libres de droit, téléchargeables immédiatement
                pour vos pubs, films, réseaux sociaux et tous vos projets. Ouvert à
                toute personne ou structure ayant besoin de musiques libres de droit.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-4"
          >
            <Link
              to="/connexion"
              data-testid="hero-cta-catalog"
              className="group flex items-center gap-3 bg-amber-500 px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-400"
            >
              Vendre ma musique
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/catalogue"
              data-testid="hero-cta-manifesto"
              className="border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.25em] text-zinc-300 transition-colors hover:border-amber-500 hover:text-amber-400"
            >
              Explorer le catalogue
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-5 text-[11px] uppercase tracking-[0.25em] text-amber-400/90"
            data-testid="hero-free-note"
          >
            Inscription gratuite — commission uniquement sur les ventes
          </motion.p>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-24 left-1/2 -translate-x-1/2"
        >
          <div className="h-12 w-px bg-gradient-to-b from-amber-500 to-transparent" />
        </motion.div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="featured-section">
        <Reveal>
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Sélection</p>
              <h2 className="mt-3 font-display text-3xl font-medium tracking-tight sm:text-5xl">
                Dernières compositions
              </h2>
            </div>
            <Link
              to="/catalogue"
              data-testid="featured-see-all"
              className="group flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 transition-colors hover:text-amber-400"
            >
              Tout le catalogue
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {tracks.slice(0, 8).map((t, i) => (
            <TrackCard key={t.track_id} track={t} index={i} />
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.08] bg-surface py-24" data-testid="artists-offer-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Pour les artistes</p>
            <h2 className="mt-3 max-w-3xl font-display text-3xl font-black tracking-tighter sm:text-5xl">
              Votre musique mérite d'être <span className="text-amber-500">payée.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
              La Console des Artistes est conçue pour les musiciens qui veulent vivre de leurs
              créations : publiez vos morceaux, fixez vos prix, touchez vos ventes. La plateforme
              s'occupe de la vitrine, des licences et des paiements.
            </p>
          </Reveal>
          <div className="mt-12">
            <Reveal>
              <div className="relative border border-amber-500/40 bg-ink p-10 text-center">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-black">Inscription gratuite</span>
                <p className="font-display text-3xl font-black tracking-tight sm:text-4xl">
                  0 € <span className="text-lg font-normal text-zinc-500">— aucun abonnement, aucun frais caché</span>
                </p>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-zinc-400">
                  L'inscription est entièrement gratuite. La plateforme se rémunère uniquement
                  par une commission de 15 % sur vos ventes : si vous ne vendez rien, vous ne payez rien.
                </p>
                <Link to="/connexion" data-testid="offer-cta-free" className="mt-8 inline-block bg-amber-500 px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-400">
                  Créer mon compte gratuit
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="manifeste" className="border-t border-white/[0.08] bg-black py-24" data-testid="manifesto-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Manifeste</p>
          </Reveal>
          <div className="mt-10 space-y-0">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="grid gap-6 border-t border-white/[0.08] py-12 md:grid-cols-12">
                  <span className="text-outline font-display text-7xl font-black md:col-span-3 md:text-8xl">{c.n}</span>
                  <div className="md:col-span-9 md:pl-8">
                    <h3 className="font-display text-2xl font-medium italic sm:text-4xl">{c.title}</h3>
                    <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="usages-section">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Pour qui</p>
          <h2 className="mt-3 font-display text-3xl font-medium tracking-tight sm:text-5xl">
            Trois terrains de jeu
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
          {USAGES.map((u, i) => (
            <Reveal key={u.title} delay={i * 0.1}>
              <div className="group h-full border border-white/[0.08] bg-surface p-8 transition-colors duration-300 hover:border-amber-500/40">
                <u.icon className="h-8 w-8 text-amber-500 transition-transform duration-300 group-hover:-translate-y-1" />
                <h3 className="mt-6 font-display text-xl font-medium sm:text-2xl">{u.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{u.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Home;
